/**
 * ContentCalendar — Marketing content planning calendar using FullCalendar.
 *
 * Shows content items (posts, shoot days) and events on a clean month/list view.
 * Replaces the previous custom CSS-grid calendar.
 */
import React, { useMemo, useRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import nlLocale from '@fullcalendar/core/locales/nl';

const STATUS_COLORS = {
  IDEA: '#94a3b8', CONCEPT: '#8b5cf6', SHOOT_PLANNING: '#d97706', SHOOT_SCHEDULED: '#ea580c',
  IN_PRODUCTION: '#2563eb', REVIEW: '#7c3aed', APPROVED: '#059669', ARCHIVED: '#d1d5db',
};

const STATUS_LABELS = {
  IDEA: 'Idee', CONCEPT: 'Concept', SHOOT_PLANNING: 'Draaidag plannen', SHOOT_SCHEDULED: 'Draaidag gepland',
  IN_PRODUCTION: 'In productie', REVIEW: 'Review', APPROVED: 'Goedgekeurd', ARCHIVED: 'Archief',
};

const POST_STATUS_COLORS = {
  SCHEDULED: '#2563eb', PUBLISHED: '#059669', CONCEPT: '#8b5cf6', INTERN: '#6b7280',
};

function parseDateStr(val) {
  if (!val) return null;
  const s = typeof val === 'string' ? val : val.toISOString();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

export default function ContentCalendar({ items, events, onItemClick }) {
  const calRef = useRef(null);

  const fcEvents = useMemo(() => {
    const result = [];

    // Content items → calendar events
    (items || []).forEach(item => {
      if (item.status === 'ARCHIVED') return;

      const plannedDate = parseDateStr(item.plannedDate);
      const shootDate = parseDateStr(item.shootDate);

      // Post date event
      if (plannedDate) {
        result.push({
          id: `post-${item.id}`,
          title: item.title,
          start: plannedDate,
          allDay: true,
          backgroundColor: POST_STATUS_COLORS[item.postStatus] || STATUS_COLORS[item.status] || '#94a3b8',
          borderColor: POST_STATUS_COLORS[item.postStatus] || STATUS_COLORS[item.status] || '#94a3b8',
          extendedProps: { type: 'post', item, status: item.postStatus || item.status },
        });
      }

      // Shoot date event (if different from planned)
      if (shootDate && shootDate !== plannedDate) {
        result.push({
          id: `shoot-${item.id}`,
          title: `🎬 ${item.title}`,
          start: shootDate,
          allDay: true,
          backgroundColor: '#d97706',
          borderColor: '#d97706',
          extendedProps: { type: 'shoot', item, status: 'DRAAIDAG' },
        });
      }
    });

    // External events → calendar events (can span multiple days)
    (events || []).forEach(evt => {
      const start = parseDateStr(evt.startDate);
      if (!start) return;
      const end = parseDateStr(evt.endDate);

      result.push({
        id: `event-${evt.id}`,
        title: evt.name,
        start,
        // FullCalendar exclusive end: add 1 day for all-day display
        end: end ? new Date(new Date(end).getTime() + 86400000).toISOString().split('T')[0] : undefined,
        allDay: true,
        backgroundColor: '#0ea5e9',
        borderColor: '#0ea5e9',
        display: 'block',
        extendedProps: { type: 'event', event: evt, status: evt.eventType },
      });
    });

    return result;
  }, [items, events]);

  return (
    <Box sx={{
      // Groter dan eerst (tool-feedback #16: "lettertype van kalender mag wat groter").
      '& .fc': { fontFamily: 'inherit', fontSize: '0.9rem' },
      '& .fc-toolbar-title': { fontSize: '1.1rem !important', fontWeight: 700 },
      '& .fc-button': {
        fontSize: '0.78rem !important', textTransform: 'none',
        bgcolor: '#fff', border: '1px solid #e2e8f0', color: '#374151',
        boxShadow: 'none !important',
      },
      '& .fc-button-primary': {
        backgroundColor: '#fff !important', borderColor: '#e2e8f0 !important',
        color: '#374151 !important',
        '&:hover': { backgroundColor: '#f8fafc !important' },
        '&.fc-button-active': {
          backgroundColor: '#6366f1 !important', borderColor: '#6366f1 !important',
          color: '#fff !important',
        },
      },
      '& .fc-day-today': { backgroundColor: '#eff6ff !important' },
      '& .fc-daygrid-day-number': { fontSize: '0.85rem', fontWeight: 500, padding: '4px 8px' },
      '& .fc-event': {
        borderRadius: '4px !important', fontSize: '0.8rem !important',
        padding: '1px 4px !important', cursor: 'pointer',
        lineHeight: '1.4 !important',
      },
      '& .fc-daygrid-event-dot': { display: 'none' },
      '& .fc-daygrid-day-frame': { minHeight: 90 },
      '& .fc-list-event-title': { fontSize: '0.9rem' },
      '& .fc-list-day-cushion': { fontWeight: 600, fontSize: '0.82rem' },
      '& .fc-col-header-cell-cushion': { fontWeight: 600, fontSize: '0.82rem', color: '#6b7280' },
      '& .fc-scrollgrid': { borderColor: '#e5e7eb' },
      '& .fc-theme-standard td, & .fc-theme-standard th': { borderColor: '#f1f5f9' },
      '& .fc-daygrid-more-link': { fontSize: '0.75rem', color: '#6366f1', fontWeight: 600 },
    }}>
      <FullCalendar
        ref={calRef}
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        locale={nlLocale}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,listMonth',
        }}
        buttonText={{ today: 'Vandaag', month: 'Maand', list: 'Lijst' }}
        events={fcEvents}
        dayMaxEvents={3}
        moreLinkText={(n) => `+${n} meer`}
        firstDay={1}
        height="auto"
        eventClick={(info) => {
          const props = info.event.extendedProps;
          if (props.item && onItemClick) onItemClick(props.item);
        }}
        eventContent={(arg) => {
          const props = arg.event.extendedProps;
          return (
            <Box sx={{
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              px: 0.3, lineHeight: 1.4,
            }}>
              <Typography component="span" sx={{
                fontSize: 'inherit', fontWeight: 500, color: 'inherit',
              }}>
                {arg.event.title}
              </Typography>
            </Box>
          );
        }}
      />
    </Box>
  );
}
